import { Directive, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudDataSource, CrudListComponent, FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_TYPE, CostCentre, RefCode } from 'assets-model';
import { BusinessUnitService, CostCentreDataSource, CostCentreService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class CostCentreSearchComponentBase extends CrudListComponent<CostCentre, string> {
  protected systemConfig: any;

  displayedColumns: string[] = [ 'ccId', 'businessUnit.buId', 'acronym', 'closed', 'createdBy', 'createdDate', 'lockVersion', 'name', 'type', 'updatedBy', 'updatedDate', 'actions'];

  public searchForm: FormGroup;

  ccIdFilter: string;
  @ViewChild('ccIdFilterInput') ccIdFilterInput: ElementRef;

  businessUnitFilter: BusinessUnit;
  businessUnitSelectionList: BusinessUnit[];
  @ViewChild('businessUnitFilterInput') businessUnitFilterInput: MatSelect;

  acronymFilter: string;
  @ViewChild('acronymFilterInput') acronymFilterInput: ElementRef;

  closedFilter: boolean;
  @ViewChild('closedFilterInput') closedFilterInput: MatSelect;

  createdByFilter: string;
  @ViewChild('createdByFilterInput') createdByFilterInput: ElementRef;

  createdDateFilter: Date;
  @ViewChild('createdDateFilterInput') createdDateFilterInput: ElementRef;
  createdDateLessThanFilter: Date;
  @ViewChild('createdDateLessThanFilterInput') createdDateLessThanFilterInput: ElementRef;
  createdDateLessThanOrEqualToFilter: Date;
  @ViewChild('createdDateLessThanOrEqualToFilterInput') createdDateLessThanOrEqualToFilterInput: ElementRef;
  createdDateGreaterThanFilter: Date;
  @ViewChild('createdDateGreaterThanFilterInput') createdDateGreaterThanFilterInput: ElementRef;
  createdDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('createdDateGreaterThanOrEqualToFilterInput') createdDateGreaterThanOrEqualToFilterInput: ElementRef;

  nameFilter: string;
  @ViewChild('nameFilterInput') nameFilterInput: ElementRef;

  typeSelectionList: RefCode[];
  typeFilter: string;
  @ViewChild('typeFilterInput') typeFilterInput: MatSelect;

  updatedByFilter: string;
  @ViewChild('updatedByFilterInput') updatedByFilterInput: ElementRef;

  updatedDateFilter: Date;
  @ViewChild('updatedDateFilterInput') updatedDateFilterInput: ElementRef;
  updatedDateLessThanFilter: Date;
  @ViewChild('updatedDateLessThanFilterInput') updatedDateLessThanFilterInput: ElementRef;
  updatedDateLessThanOrEqualToFilter: Date;
  @ViewChild('updatedDateLessThanOrEqualToFilterInput') updatedDateLessThanOrEqualToFilterInput: ElementRef;
  updatedDateGreaterThanFilter: Date;
  @ViewChild('updatedDateGreaterThanFilterInput') updatedDateGreaterThanFilterInput: ElementRef;
  updatedDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('updatedDateGreaterThanOrEqualToFilterInput') updatedDateGreaterThanOrEqualToFilterInput: ElementRef;


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected costCentreService: CostCentreService,
    protected businessUnitService: BusinessUnitService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(COST_CENTRE_TYPE, route, router, userStorage, dialog, costCentreService, injector);

    this.searchForm = this.fb.group({
      ccId: [null],
      businessUnit: [null],
      acronym: [null],
      closed: [null],
      createdBy: [null],
      createdDateLessThan: [null],
      createdDateLessThanOrEqualTo: [null],
      createdDateGreaterThan: [null],
      createdDateGreaterThanOrEqualTo: [null],
      createdDate: [null],
      name: [null],
      type: [null],
      updatedBy: [null],
      updatedDateLessThan: [null],
      updatedDateLessThanOrEqualTo: [null],
      updatedDateGreaterThan: [null],
      updatedDateGreaterThanOrEqualTo: [null],
      updatedDate: [null]
    });
  }

  createDataSource(): CrudDataSource<CostCentre> {
    return new CostCentreDataSource(this.costCentreService);
  }

  createServiceArguments(): any {
    return {
      ccId: this.ccIdFilter,
      buId: this.businessUnitFilter && this.businessUnitFilter.buId,
      acronym: this.acronymFilter,
      closed: this.closedFilter,
      createdBy: this.createdByFilter,
      createdDateLessThan: this.createdDateLessThanFilter,
      createdDateLessThanOrEqualTo: this.createdDateLessThanOrEqualToFilter,
      createdDateGreaterThan: this.createdDateGreaterThanFilter,
      createdDateGreaterThanOrEqualTo: this.createdDateGreaterThanOrEqualToFilter,
      createdDate: this.createdDateFilter,
      name: this.nameFilter,
      type: this.typeFilter,
      updatedBy: this.updatedByFilter,
      updatedDateLessThan: this.updatedDateLessThanFilter,
      updatedDateLessThanOrEqualTo: this.updatedDateLessThanOrEqualToFilter,
      updatedDateGreaterThan: this.updatedDateGreaterThanFilter,
      updatedDateGreaterThanOrEqualTo: this.updatedDateGreaterThanOrEqualToFilter,
      updatedDate: this.updatedDateFilter
    };
  }
  
  clearFiltersBeforeLoadPage() {
    this.restoreFormFromParams(this.searchForm);
  }
  
  clearFilterVariables() {
    this.ccIdFilter = null;
    this.businessUnitFilter = null;
    this.acronymFilter = null;
    this.closedFilter = null;
    this.createdByFilter = null;
    this.createdDateLessThanFilter = null;
    this.createdDateLessThanOrEqualToFilter = null;
    this.createdDateGreaterThanFilter = null;
    this.createdDateGreaterThanOrEqualToFilter = null;
    this.createdDateFilter = null;
    this.nameFilter = null;
    this.typeFilter = null;
    this.updatedByFilter = null;
    this.updatedDateLessThanFilter = null;
    this.updatedDateLessThanOrEqualToFilter = null;
    this.updatedDateGreaterThanFilter = null;
    this.updatedDateGreaterThanOrEqualToFilter = null;
    this.updatedDateFilter = null;
  }
  
  clearFilters() {
    this.clearFilterVariables();
    this.clearFiltersBeforeLoadPage();
    this.loadPage();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedInputEventListener(this.ccIdFilterInput);
    this.addSearchOnChangedSelectEventListener(this.businessUnitFilterInput);
    this.addSearchOnChangedInputEventListener(this.acronymFilterInput);
    this.addSearchOnChangedSelectEventListener(this.closedFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdByFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateFilterInput);
    this.addSearchOnChangedInputEventListener(this.nameFilterInput);
    this.addSearchOnChangedSelectEventListener(this.typeFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedByFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateFilterInput);
    this.formStorage.rememberFormChanges(this.searchForm);
  }

  ngOnInit() {
    super.ngOnInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }

  protected initExportOptions(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initExportOptions(config);
  }
  
  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    return {
      ccId: (value: string) => self.ccIdFilter = value,
      businessUnit: (value: BusinessUnit) => { self.businessUnitFilter = value; this.businessUnitChanged(value, false); },
      acronym: (value: string) => self.acronymFilter = value,
      closed: (value: boolean) => self.closedFilter = value,
      createdBy: (value: string) => self.createdByFilter = value,
      createdDateLessThan: (value: Date) => self.createdDateLessThanFilter = value,
      createdDateLessThanOrEqualTo: (value: Date) => self.createdDateLessThanOrEqualToFilter = value,
      createdDateGreaterThan: (value: Date) => self.createdDateGreaterThanFilter = value,
      createdDateGreaterThanOrEqualTo: (value: Date) => self.createdDateGreaterThanOrEqualToFilter = value,
      createdDate: (value: Date) => self.createdDateFilter = value,
      name: (value: string) => self.nameFilter = value,
      type: (value: string) => self.typeFilter = value,
      updatedBy: (value: string) => self.updatedByFilter = value,
      updatedDateLessThan: (value: Date) => self.updatedDateLessThanFilter = value,
      updatedDateLessThanOrEqualTo: (value: Date) => self.updatedDateLessThanOrEqualToFilter = value,
      updatedDateGreaterThan: (value: Date) => self.updatedDateGreaterThanFilter = value,
      updatedDateGreaterThanOrEqualTo: (value: Date) => self.updatedDateGreaterThanOrEqualToFilter = value,
      updatedDate: (value: Date) => self.updatedDateFilter = value
    }
  }
  
  restoreFormChanges() {
    const self = this;

    // IMPORTANT that the restoreFormChanges call occurs before the initial search - typically best done in onInitAsyncComplete
    // Whereever a form control binds with [ngModel] to a reactive form, ngModel takes precedence,
    // and needs it's own setter function to set the scope attribute bound
    this.formStorage.restoreFormChanges(this.searchForm, this.transformAllPropertiesToModelSetters());
  }

  ccIdChanging(value: string, unsetDependents: boolean = true) {}
  
  ccIdChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = data.items;
      }
    );
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  

  acronymChanging(value: string, unsetDependents: boolean = true) {}
  
  acronymChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  closedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  closedChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  nameChanging(value: string, unsetDependents: boolean = true) {}
  
  nameChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  typeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('COST_CENTRE_TYPE').subscribe(
      data => {
        this.typeSelectionList = data;
      }
    );
  }

  typeChanging(value: string, unsetDependents: boolean = true) {}
  
  typeChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disabledTooltip(element) {
    if (!element) {
      return true;
    }
    return element.valid || !element.touched;
  }

  get ccId() {
    return this.searchForm && this.searchForm.get('ccId');
  }

  get businessUnit() {
    return this.searchForm && this.searchForm.get('businessUnit');
  }

  get acronym() {
    return this.searchForm && this.searchForm.get('acronym');
  }

  get closed() {
    return this.searchForm && this.searchForm.get('closed');
  }

  get createdBy() {
    return this.searchForm && this.searchForm.get('createdBy');
  }

  get createdDate() {
    return this.searchForm && this.searchForm.get('createdDate');
  }

  get createdDateLessThan() {
    return this.searchForm && this.searchForm.get('createdDateLessThan');
  }
  
  get createdDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('createdDateLessThanOrEqualTo');
  }
  
  get createdDateGreaterThan() {
    return this.searchForm && this.searchForm.get('createdDateGreaterThan');
  }
  
  get createdDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('createdDateGreaterThanOrEqualTo');
  }

  get name() {
    return this.searchForm && this.searchForm.get('name');
  }

  get type() {
    return this.searchForm && this.searchForm.get('type');
  }

  get updatedBy() {
    return this.searchForm && this.searchForm.get('updatedBy');
  }

  get updatedDate() {
    return this.searchForm && this.searchForm.get('updatedDate');
  }

  get updatedDateLessThan() {
    return this.searchForm && this.searchForm.get('updatedDateLessThan');
  }
  
  get updatedDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('updatedDateLessThanOrEqualTo');
  }
  
  get updatedDateGreaterThan() {
    return this.searchForm && this.searchForm.get('updatedDateGreaterThan');
  }
  
  get updatedDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('updatedDateGreaterThanOrEqualTo');
  }


}



